"use client"

/**
 * SecondaryPanel — nested rail between the primary icon sidebar and content.
 * Full width shows hub scope nav; **compact** matches the primary sidebar icon rail (`w-12`).
 *
 * Chrome uses {@link NestedSecondaryPanelShell}. Library body stays in
 * `library-secondary-nav.tsx` (domain-specific), not duplicated here.
 */

import * as React from "react"
import { useLocation } from "react-router"
import { useRegisterNavFlyoutToggle, useSidebar } from "@/components/ui/sidebar"
import { Tip } from "@/components/ui/tip"
import { Button } from "@/components/ui/button"
import { LibrarySecondaryNav } from "@/components/library-secondary-nav"
import { LearningActivitiesSecondaryNav } from "@/components/learning-activities-secondary-nav"
import {
  NestedSecondaryPanelShell,
  SECONDARY_PANEL_WIDTH_DEFAULT,
  SECONDARY_PANEL_WIDTH_KEY,
  SECONDARY_PANEL_WIDTH_MAX,
  SECONDARY_PANEL_WIDTH_MIN,
  clampSecondaryPanelWidth,
} from "@/components/templates/nested-secondary-panel-shell"
import {
  ResizableHandle,
  ResizablePanel,
} from "@/components/ui/resizable"
import { useShellRailWidth } from "@exxatdesignux/ui/hooks/use-shell-rail-width"
import { NAV_FLYOUT_SCROLL_BODY } from "@exxatdesignux/ui/lib/nav-flyout-inset"
import { Shortcut } from "@/components/ui/dropdown-menu"
import { useIsMobile } from "@/hooks/use-mobile"
import { useSidebarReflowZoom } from "@/hooks/use-sidebar-reflow-zoom"
import { useAskLeoDockedPanelOpen } from "@/hooks/use-ask-leo-docked-panel-open"
import { useAskLeo } from "@/components/ask-leo-context"
import {
  isLibrarySecondaryPanelRoute,
  isLibrarySecondaryPanelVisible,
  shouldLibrarySecondaryPanelBeOpen,
} from "@/lib/library-nav"
import {
  isLearningActivitiesCourseDetailPath,
  isLearningActivitiesSecondaryPanelRoute,
  isLearningActivitiesSecondaryPanelVisible,
  shouldLearningActivitiesSecondaryPanelBeOpen,
} from "@/lib/learning-activities-nav"
import { isSidebarHiddenPath } from "@/lib/focus-workflow"
import {
  GRADING_SUBMISSIONS_SECONDARY_PANEL_ID,
  isSubmissionDetailSecondaryPanelVisible,
  shouldSubmissionDetailSecondaryPanelBeOpen,
} from "@/lib/submission-detail-nav"
import { SubmissionsSecondaryNav } from "@/components/grading/submissions-secondary-nav"
import {
  AssessmentQuestionBankSecondaryNav,
  type AssessmentQuestionBankBridge,
} from "@/components/assessment-structure/assessment-question-bank-secondary-nav"
import { SecondaryHubNavCompactShell } from "@/components/sidebar/secondary-hub-nav-primitives"
import {
  IMPORT_REVIEW_SECONDARY_PANEL_ID,
  isImportReviewSecondaryPanelVisible,
  shouldCloseStaleImportReviewPanel,
  shouldImportReviewSecondaryPanelBeOpen,
} from "@/lib/import-review-nav"
import {
  ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID,
  isAssessmentQuestionBankSecondaryPanelRoute,
  isAssessmentQuestionBankSecondaryPanelVisible,
  shouldAssessmentQuestionBankSecondaryPanelBeOpen,
} from "@/lib/assessment-question-bank-nav"
import type {
  AssessmentGradingState,
  GradingSubmission,
} from "@/lib/mock/exam-grading"
import { cn } from "@/lib/utils"
import type { LibraryItem } from "@/lib/mock/library"
import type { LibraryFolder } from "@/lib/mock/library-folders"

export type LibraryFolderBridge = {
  folders: LibraryFolder[]
  onFoldersChange: React.Dispatch<React.SetStateAction<LibraryFolder[]>>
  onItemsChange: React.Dispatch<React.SetStateAction<LibraryItem[]>>
  /** Rolled-up bank question counts keyed by folder id (includes descendants). */
  folderItemCounts: Readonly<Record<string, number>>
  /** Precomputed All / My / Favorites / Imported scope badges for the nav. */
  scopeCounts: {
    all: number
    my: number
    favorites: number
    imported: number
  }
}

export type LibraryAccessBridge = {
  openManageAccess: () => void
}

export type LearningActivitiesFolderBridge = {
  folders: LibraryFolder[]
  onFoldersChange: React.Dispatch<React.SetStateAction<LibraryFolder[]>>
}

export type GradingSubmissionsBridge = {
  state: AssessmentGradingState
  submissions: GradingSubmission[]
  currentSubmissionId: string
  onSelect: (submission: GradingSubmission) => void
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

export type ClosePanelOptions = {
  /**
   * Main app sidebar after the secondary panel closes.
   * - `restore` (default): snap back to the user's saved preference
   *   (`sidebar_state_v2` cookie, or `defaultOpen` if unset). Use this when
   *   leaving a page whose secondary panel caused an incidental collapse — the
   *   rail returns to the state the user explicitly set elsewhere via ⌘B / the
   *   sidebar button.
   * - `leave`: only clear the active panel — keep the rail in whatever state it
   *   was in (rare; prefer `restore`).
   * - `expand`: force the full primary rail open. Use only when the product
   *   explicitly wants the wide rail after dismiss.
   * - `collapse`: keep the icon rail. Use when the next route also wants compact.
   */
  mainSidebar?: "restore" | "expand" | "collapse" | "leave"
}

export type OpenPanelOptions = {
  /** Icon-only nested rail — used on focus shells (`/library/new`) so scope nav stays compact. */
  compact?: boolean
}

interface SecondaryPanelContextValue {
  /** Currently active panel id, or null if none */
  activePanel: string | null
  /**
   * Focus shell (`NewFocusTemplate`) with nested library scope nav — primary
   * sidebar is hidden; secondary stays on the compact icon rail.
   */
  focusShellSupersedesPrimarySidebar: boolean
  /** Open a panel by id. Pass `{ compact: true }` to keep the icon rail. */
  openPanel: (id: string, opts?: OpenPanelOptions) => void
  /** Close the panel (programmatic / route cleanup). */
  closePanel: (opts?: ClosePanelOptions) => void
  /** Icon-only nested rail while the panel stays “open”. Cleared by {@link openPanel} / {@link closePanel}. */
  secondaryPanelCompact: boolean
  /** Narrow icon rail (primary-sidebar-style); keeps {@link activePanel} mounted. */
  collapseActiveSecondaryPanel: () => void
  /** Library folder tree shared with the secondary nav while the hub is mounted. */
  libraryFolderBridge: LibraryFolderBridge | null
  setLibraryFolderBridge: (bridge: LibraryFolderBridge | null) => void
  /** Opens the hub collaborators sheet from the secondary nav. */
  libraryAccessBridge: LibraryAccessBridge | null
  setLibraryAccessBridge: (bridge: LibraryAccessBridge | null) => void
  /** Learning activities group tree shared with the secondary nav while the hub is mounted. */
  learningActivitiesFolderBridge: LearningActivitiesFolderBridge | null
  setLearningActivitiesFolderBridge: (bridge: LearningActivitiesFolderBridge | null) => void
  /** Submission detail roster while grading a single assessment. */
  gradingSubmissionsBridge: GradingSubmissionsBridge | null
  setGradingSubmissionsBridge: (bridge: GradingSubmissionsBridge | null) => void
  /** Add Questions bank scopes + blueprint + section roster while the pool is mounted. */
  assessmentQuestionBankBridge: AssessmentQuestionBankBridge | null
  setAssessmentQuestionBankBridge: (
    bridge: AssessmentQuestionBankBridge | null
  ) => void
  /** Host for the import-review question list while that panel is open. */
  importReviewListHost: HTMLElement | null
  setImportReviewListHost: (host: HTMLElement | null) => void
  /**
   * Flyout stack (mobile / ≥200% zoom): temporarily hide the secondary overlay
   * so the primary sidebar underneath is reachable. Cleared by {@link openPanel}.
   */
  secondaryFlyoutHidden: boolean
  hideSecondaryFlyout: () => void
  showSecondaryFlyout: () => void
  /** Flyout sheet visible (mobile / high zoom). False when user closed the sheet but stayed on the hub. */
  secondaryFlyoutVisible: boolean
  /** Hide the scope sheet only — keeps {@link activePanel} for `/library/all`. */
  closeSecondaryFlyout: () => void
}

const SecondaryPanelContext = React.createContext<SecondaryPanelContextValue>({
  activePanel: null,
  focusShellSupersedesPrimarySidebar: false,
  openPanel: () => {},
  closePanel: () => {},
  secondaryPanelCompact: false,
  collapseActiveSecondaryPanel: () => {},
  libraryFolderBridge: null,
  setLibraryFolderBridge: () => {},
  libraryAccessBridge: null,
  setLibraryAccessBridge: () => {},
  learningActivitiesFolderBridge: null,
  setLearningActivitiesFolderBridge: () => {},
  gradingSubmissionsBridge: null,
  setGradingSubmissionsBridge: () => {},
  assessmentQuestionBankBridge: null,
  setAssessmentQuestionBankBridge: () => {},
  importReviewListHost: null,
  setImportReviewListHost: () => {},
  secondaryFlyoutHidden: false,
  hideSecondaryFlyout: () => {},
  showSecondaryFlyout: () => {},
  secondaryFlyoutVisible: true,
  closeSecondaryFlyout: () => {},
})

export function useSecondaryPanel() {
  return React.useContext(SecondaryPanelContext)
}

export function SecondaryPanelProvider({ children }: { children: React.ReactNode }) {
  const [activePanel, setActivePanel] = React.useState<string | null>(null)
  const [secondaryPanelCompact, setSecondaryPanelCompact] = React.useState(false)
  const [libraryFolderBridge, setLibraryFolderBridge] =
    React.useState<LibraryFolderBridge | null>(null)
  const [libraryAccessBridge, setLibraryAccessBridge] =
    React.useState<LibraryAccessBridge | null>(null)
  const [learningActivitiesFolderBridge, setLearningActivitiesFolderBridge] =
    React.useState<LearningActivitiesFolderBridge | null>(null)
  const [gradingSubmissionsBridge, setGradingSubmissionsBridge] =
    React.useState<GradingSubmissionsBridge | null>(null)
  const [assessmentQuestionBankBridge, setAssessmentQuestionBankBridge] =
    React.useState<AssessmentQuestionBankBridge | null>(null)
  const [importReviewListHost, setImportReviewListHost] =
    React.useState<HTMLElement | null>(null)
  const [secondaryFlyoutHidden, setSecondaryFlyoutHidden] = React.useState(false)
  const [secondaryFlyoutVisible, setSecondaryFlyoutVisible] = React.useState(true)
  const { pathname, search } = useLocation()
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  const navFlyout = isMobile || reflowZoom
  const librarySecondaryPanelRoute = isLibrarySecondaryPanelRoute(pathname)
  const learningActivitiesSecondaryPanelRoute = isLearningActivitiesSecondaryPanelRoute(pathname)
  const { setOpen, restoreSavedOpen, open: mainSidebarOpen } = useSidebar()
  const { setOpen: setAskLeoOpen } = useAskLeo()
  const askLeoDockedOpen = useAskLeoDockedPanelOpen()
  const effectiveSecondaryCompact =
    !navFlyout && (secondaryPanelCompact || askLeoDockedOpen)

  /**
   * WCAG 1.4.10 reflow (≤320px width, ≥200% zoom, or short viewport) — same
   * `useSidebarReflowZoom` signal the primary sidebar uses. At that scale the
   * nested rail must **not** stay pinned as an icon strip beside content. Hide
   * it until the user opens Back / Main menu (one flyout layer at a time).
   */
  const wasReflowZoomRef = React.useRef(false)
  React.useEffect(() => {
    if (reflowZoom && !wasReflowZoomRef.current && activePanel) {
      setOpen(false, { persist: false })
    }
    wasReflowZoomRef.current = reflowZoom
  }, [reflowZoom, activePanel, setOpen])

  /** Icon-only compact rail is desktop-only — flyout sheets always show labels. */
  React.useEffect(() => {
    if (navFlyout && secondaryPanelCompact) {
      setSecondaryPanelCompact(false)
    }
  }, [navFlyout, secondaryPanelCompact])

  const hideSecondaryFlyout = React.useCallback(() => {
    setSecondaryFlyoutHidden(true)
    setOpen(true, { persist: false })
  }, [setOpen])

  const showSecondaryFlyout = React.useCallback(() => {
    setSecondaryFlyoutHidden(false)
    setSecondaryPanelCompact(false)
    setSecondaryFlyoutVisible(true)
    setOpen(false, { persist: false })
    setAskLeoOpen(false)
  }, [setOpen, setAskLeoOpen])

  /** Scope sheet visible → keep primary flyout closed (not stacked). */
  React.useEffect(() => {
    if (!navFlyout || !activePanel || secondaryFlyoutHidden || !secondaryFlyoutVisible) {
      return
    }
    setOpen(false, { persist: false })
  }, [navFlyout, activePanel, secondaryFlyoutHidden, secondaryFlyoutVisible, setOpen])

  const closeSecondaryFlyout = React.useCallback(() => {
    setSecondaryPanelCompact(false)
    setSecondaryFlyoutHidden(false)
    setSecondaryFlyoutVisible(false)
    setOpen(false, { persist: false })
  }, [setOpen])

  const openPanel = React.useCallback(
    (id: string, opts?: OpenPanelOptions) => {
      setSecondaryPanelCompact(opts?.compact ?? false)
      setSecondaryFlyoutHidden(false)
      setSecondaryFlyoutVisible(true)
      setActivePanel(id)
      setOpen(false, { persist: false })
      setAskLeoOpen(false)
    },
    [setOpen, setAskLeoOpen],
  )

  const closePanel = React.useCallback((opts?: ClosePanelOptions) => {
    setSecondaryPanelCompact(false)
    setSecondaryFlyoutHidden(false)
    setSecondaryFlyoutVisible(true)
    setActivePanel(null)
    const mainSidebar = opts?.mainSidebar ?? "restore"
    if (mainSidebar === "leave") return
    if (mainSidebar === "restore") {
      restoreSavedOpen()
      return
    }
    if (mainSidebar === "collapse") {
      setOpen(false, { persist: false })
      return
    }
    setOpen(true, { persist: false })
  }, [restoreSavedOpen, setOpen])

  /** URL is source of truth for nested scope rails (avoids empty shell / stuck panel races). */
  React.useEffect(() => {
    if (shouldSubmissionDetailSecondaryPanelBeOpen(pathname)) {
      // Record-detail chrome hides the primary sidebar but keeps this nested
      // rail — do not fall through to `isSidebarHiddenPath` (that closes panels).
      if (
        gradingSubmissionsBridge &&
        activePanel !== GRADING_SUBMISSIONS_SECONDARY_PANEL_ID
      ) {
        openPanel(GRADING_SUBMISSIONS_SECONDARY_PANEL_ID, { compact: true })
      }
      return
    }
    if (shouldImportReviewSecondaryPanelBeOpen(pathname, search)) {
      if (activePanel !== IMPORT_REVIEW_SECONDARY_PANEL_ID) {
        openPanel(IMPORT_REVIEW_SECONDARY_PANEL_ID)
      }
      return
    }
    if (shouldCloseStaleImportReviewPanel({ pathname, search, activePanel })) {
      closePanel({ mainSidebar: "leave" })
      return
    }
    if (shouldAssessmentQuestionBankSecondaryPanelBeOpen(pathname, search)) {
      if (
        assessmentQuestionBankBridge &&
        activePanel !== ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID
      ) {
        openPanel(ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID, { compact: true })
      }
      return
    }
    if (isSidebarHiddenPath(pathname, search)) {
      if (activePanel) {
        closePanel({ mainSidebar: "leave" })
      }
      return
    }
    if (shouldLibrarySecondaryPanelBeOpen(pathname, search)) {
      if (activePanel !== "library") {
        openPanel("library")
      }
      return
    }
    if (shouldLearningActivitiesSecondaryPanelBeOpen(pathname)) {
      if (activePanel !== "learning-activities") {
        openPanel("learning-activities")
      }
      return
    }
    if (
      activePanel === "library" ||
      activePanel === "learning-activities" ||
      activePanel === GRADING_SUBMISSIONS_SECONDARY_PANEL_ID ||
      activePanel === ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID ||
      activePanel === IMPORT_REVIEW_SECONDARY_PANEL_ID
    ) {
      // Record detail needs the icon rail (more canvas). Leaving the primary
      // open from a compact-secondary hub visit would keep labels expanded.
      closePanel({
        mainSidebar: isLearningActivitiesCourseDetailPath(pathname)
          ? "collapse"
          : "leave",
      })
    }
  }, [
    pathname,
    search,
    activePanel,
    gradingSubmissionsBridge,
    assessmentQuestionBankBridge,
    openPanel,
    closePanel,
  ])

  const handleSecondaryNavFlyoutToggle = React.useCallback((): boolean => {
    if (!navFlyout) return false

    if (librarySecondaryPanelRoute && activePanel !== "library") {
      openPanel("library")
      return true
    }

    if (learningActivitiesSecondaryPanelRoute && activePanel !== "learning-activities") {
      openPanel("learning-activities")
      return true
    }

    if (
      isAssessmentQuestionBankSecondaryPanelRoute(pathname) &&
      activePanel !== ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID
    ) {
      openPanel(ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID)
      return true
    }

    if (
      activePanel !== "library" &&
      activePanel !== "learning-activities" &&
      activePanel !== ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID
    ) {
      return false
    }

    if (secondaryFlyoutHidden) {
      if (mainSidebarOpen) {
        setOpen(false, { persist: false })
      } else {
        showSecondaryFlyout()
      }
      return true
    }

    if (secondaryFlyoutVisible) {
      closeSecondaryFlyout()
    } else {
      setSecondaryPanelCompact(false)
      setSecondaryFlyoutVisible(true)
      setOpen(false, { persist: false })
    }
    return true
  }, [
    navFlyout,
    librarySecondaryPanelRoute,
    learningActivitiesSecondaryPanelRoute,
    pathname,
    activePanel,
    secondaryFlyoutHidden,
    secondaryFlyoutVisible,
    mainSidebarOpen,
    showSecondaryFlyout,
    closeSecondaryFlyout,
    openPanel,
    setOpen,
  ])

  useRegisterNavFlyoutToggle(handleSecondaryNavFlyoutToggle)

  const collapseActiveSecondaryPanel = React.useCallback(() => {
    setSecondaryPanelCompact(true)
  }, [])

  /**
   * Desktop: when the user expands the primary sidebar (⌘B) while a scope rail
   * is open, collapse the secondary panel to its icon strip so both rails are
   * not fighting for width. Expanding labels via « still collapses primary
   * (`openPanel`).
   */
  React.useEffect(() => {
    if (navFlyout || !activePanel || !mainSidebarOpen || secondaryPanelCompact) {
      return
    }
    if (activePanel === IMPORT_REVIEW_SECONDARY_PANEL_ID) {
      return
    }
    setSecondaryPanelCompact(true)
  }, [navFlyout, activePanel, mainSidebarOpen, secondaryPanelCompact])

  const focusShellSupersedesPrimarySidebar = isSidebarHiddenPath(pathname, search)

  const value = React.useMemo(
    () => ({
      activePanel,
      focusShellSupersedesPrimarySidebar,
      openPanel,
      closePanel,
      secondaryPanelCompact: effectiveSecondaryCompact,
      collapseActiveSecondaryPanel,
      libraryFolderBridge,
      setLibraryFolderBridge,
      libraryAccessBridge,
      setLibraryAccessBridge,
      learningActivitiesFolderBridge,
      setLearningActivitiesFolderBridge,
      gradingSubmissionsBridge,
      setGradingSubmissionsBridge,
      assessmentQuestionBankBridge,
      setAssessmentQuestionBankBridge,
      importReviewListHost,
      setImportReviewListHost,
      secondaryFlyoutHidden,
      hideSecondaryFlyout,
      showSecondaryFlyout,
      secondaryFlyoutVisible,
      closeSecondaryFlyout,
    }),
    [
      activePanel,
      focusShellSupersedesPrimarySidebar,
      openPanel,
      closePanel,
      effectiveSecondaryCompact,
      collapseActiveSecondaryPanel,
      libraryFolderBridge,
      libraryAccessBridge,
      learningActivitiesFolderBridge,
      gradingSubmissionsBridge,
      assessmentQuestionBankBridge,
      importReviewListHost,
      secondaryFlyoutHidden,
      hideSecondaryFlyout,
      showSecondaryFlyout,
      secondaryFlyoutVisible,
      closeSecondaryFlyout,
    ],
  )

  return (
    <SecondaryPanelContext.Provider value={value}>
      {children}
    </SecondaryPanelContext.Provider>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// SecondaryPanel — the actual rendered panel
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The panel's own title: a heading, in sans.
 *
 * Not `font-heading`. Ivy Presto is the page `<h1>`'s and nothing else's — the base
 * stylesheet says so in as many words — and a 20px serif here put a second display
 * title in the viewport competing with the real one a few hundred pixels to its
 * right, which is the one naming the record the reader came for.
 *
 * Not the uppercase micro-label either, which is what it briefly became. That is the
 * voice of `SidebarGroupLabel` and of `SecondaryHubNavSectionHeader`, and the second
 * of those renders the section headings *inside* this panel — "Folders", "Group".
 * A panel titled in the same case and size as the sections it contains flattens the
 * two into one level, and the reader has nothing to tell the panel's name from a
 * heading over three of its rows.
 *
 * `SectionHeading`'s treatment instead (`components/product-home/section-heading.tsx`),
 * which is the sentence-case sans heading this design system already uses to head a
 * block. It leaves three legible steps in the column: 16 for the panel, 14 for the
 * rows, 12 uppercase for the section eyebrows.
 */
const PANEL_TITLE_CLASS =
  "m-0 min-w-0 truncate py-0.5 text-base font-semibold tracking-tight text-sidebar-foreground"

export function SecondaryPanelFlyoutHeader({
  title,
  flyout,
  side = "left",
  onCollapse,
  onCloseFlyout,
}: {
  title?: string
  flyout: boolean
  side?: "left" | "right"
  onCollapse?: () => void
  onCloseFlyout?: () => void
}) {
  const { collapseActiveSecondaryPanel, closeSecondaryFlyout, hideSecondaryFlyout } =
    useSecondaryPanel()
  const collapse = onCollapse ?? collapseActiveSecondaryPanel
  const closeFlyout = onCloseFlyout ?? closeSecondaryFlyout
  const collapseIcon =
    side === "right" ? "fa-light fa-angles-right" : "fa-light fa-angles-left"

  return (
    <div
      className={cn(
        flyout
          ? "flex shrink-0 flex-col gap-1 border-b border-sidebar-border px-2 pb-2 pt-1.5"
          : "flex h-(--header-height) shrink-0 items-center justify-between gap-2 px-3",
      )}
    >
      {flyout ? (
        <Button
          type="button"
          variant="ghost"
          className="h-8 w-full justify-start gap-2 px-2 text-sidebar-foreground"
          onClick={hideSecondaryFlyout}
          aria-label="Main menu"
        >
          <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden="true">
            <i className="fa-light fa-arrow-left" aria-hidden="true" />
          </span>
          <span className="text-sm font-medium">Main menu</span>
        </Button>
      ) : null}
      {flyout ? (
        <div className="flex items-center justify-between gap-2 px-2">
          {title ? (
            <h2 className={PANEL_TITLE_CLASS}>{title}</h2>
          ) : (
            <span className="sr-only">Panel</span>
          )}
          <Tip label="Close navigation" side="bottom">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => closeFlyout()}
              aria-label="Close navigation"
            >
              <i className="fa-light fa-xmark" aria-hidden="true" />
            </Button>
          </Tip>
        </div>
      ) : (
        <>
          {title ? (
            <h2 className={cn(PANEL_TITLE_CLASS, "flex-1")}>{title}</h2>
          ) : (
            <span className="sr-only">Panel</span>
          )}
          <Tip label="Collapse to icons" side="bottom">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => collapse()}
              aria-label="Collapse to icons"
            >
              <i className={collapseIcon} aria-hidden="true" />
            </Button>
          </Tip>
        </>
      )}
    </div>
  )
}

function LibraryPanel() {
  const { secondaryPanelCompact } = useSecondaryPanel()
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  const navFlyout = isMobile || reflowZoom
  const showCompactRail = secondaryPanelCompact && !navFlyout

  if (showCompactRail) {
    return <LibrarySecondaryNav />
  }

  return (
    <>
      <SecondaryPanelFlyoutHeader title="Question Bank" flyout={navFlyout} />
      <div className={NAV_FLYOUT_SCROLL_BODY}>
        <LibrarySecondaryNav />
      </div>
    </>
  )
}

function LearningActivitiesPanelShell() {
  const { secondaryPanelCompact } = useSecondaryPanel()
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  const navFlyout = isMobile || reflowZoom
  const showCompactRail = secondaryPanelCompact && !navFlyout

  if (showCompactRail) {
    return <LearningActivitiesSecondaryNav />
  }

  return (
    <>
      <SecondaryPanelFlyoutHeader title="Learning activities" flyout={navFlyout} />
      <div className={NAV_FLYOUT_SCROLL_BODY}>
        <LearningActivitiesSecondaryNav />
      </div>
    </>
  )
}

function GradingSubmissionsPanelShell() {
  const { secondaryPanelCompact } = useSecondaryPanel()
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  const navFlyout = isMobile || reflowZoom
  const showCompactRail = secondaryPanelCompact && !navFlyout

  if (showCompactRail) {
    return <SubmissionsSecondaryNav />
  }

  return (
    <>
      <SecondaryPanelFlyoutHeader title="Submissions" flyout={navFlyout} />
      <div className={NAV_FLYOUT_SCROLL_BODY}>
        <SubmissionsSecondaryNav />
      </div>
    </>
  )
}

function AssessmentQuestionBankPanelShell() {
  const { secondaryPanelCompact } = useSecondaryPanel()
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  const navFlyout = isMobile || reflowZoom
  const showCompactRail = secondaryPanelCompact && !navFlyout

  if (showCompactRail) {
    return <AssessmentQuestionBankSecondaryNav />
  }

  return (
    <>
      <SecondaryPanelFlyoutHeader flyout={navFlyout} />
      <div className={NAV_FLYOUT_SCROLL_BODY}>
        <AssessmentQuestionBankSecondaryNav />
      </div>
    </>
  )
}

function ImportReviewPanelShell() {
  const { secondaryPanelCompact, setImportReviewListHost, openPanel } =
    useSecondaryPanel()
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  const navFlyout = isMobile || reflowZoom
  const showCompactRail = secondaryPanelCompact && !navFlyout
  const hostNodeRef = React.useRef<HTMLElement | null>(null)
  const assignHost = React.useCallback(
    (node: HTMLElement | null) => {
      if (hostNodeRef.current === node) return
      hostNodeRef.current = node
      setImportReviewListHost(node)
    },
    [setImportReviewListHost]
  )

  React.useEffect(() => {
    return () => setImportReviewListHost(null)
  }, [setImportReviewListHost])

  return (
    <>
      {showCompactRail ? (
        <SecondaryHubNavCompactShell
          ariaLabel="Questions in this review"
          panelId={IMPORT_REVIEW_SECONDARY_PANEL_ID}
          side="left"
        >
          <li className="flex w-full justify-center" role="none">
            <Tip label="Questions" side="right">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-9"
                aria-label="Questions"
                onClick={() => openPanel(IMPORT_REVIEW_SECONDARY_PANEL_ID)}
              >
                <i className="fa-solid fa-list" aria-hidden />
              </Button>
            </Tip>
          </li>
        </SecondaryHubNavCompactShell>
      ) : (
        <SecondaryPanelFlyoutHeader title="Questions" flyout={navFlyout} />
      )}
      <div
        ref={assignHost}
        className={
          showCompactRail
            ? "hidden"
            : `${NAV_FLYOUT_SCROLL_BODY} flex min-h-0 flex-1 flex-col overflow-hidden`
        }
      />
    </>
  )
}

/** Register panel components by id when a route opts into `secondaryPanel` in nav. */
const PANELS: Record<string, React.FC> = {
  "library": LibraryPanel,
  "learning-activities": LearningActivitiesPanelShell,
  "grading-submissions": GradingSubmissionsPanelShell,
  [ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID]:
    AssessmentQuestionBankPanelShell,
  [IMPORT_REVIEW_SECONDARY_PANEL_ID]: ImportReviewPanelShell,
}

export function SecondaryPanel() {
  const { pathname, search } = useLocation()
  const {
    activePanel,
    secondaryPanelCompact,
    secondaryFlyoutHidden,
    secondaryFlyoutVisible,
    closeSecondaryFlyout,
    gradingSubmissionsBridge,
    assessmentQuestionBankBridge,
  } = useSecondaryPanel()
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  /** Render from the route when on a scoped library hub even if `activePanel` lags (Strict Mode / nav races). */
  const panelId = isLibrarySecondaryPanelVisible(pathname, activePanel, search)
    ? "library"
    : isLearningActivitiesSecondaryPanelVisible(pathname, activePanel)
      ? "learning-activities"
      : isSubmissionDetailSecondaryPanelVisible(pathname, activePanel)
        ? "grading-submissions"
        : isImportReviewSecondaryPanelVisible(pathname, search, activePanel)
          ? IMPORT_REVIEW_SECONDARY_PANEL_ID
          : isAssessmentQuestionBankSecondaryPanelVisible(
              pathname,
              search,
              activePanel
            )
          ? ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID
          : activePanel
  // Folder tree and import review wrap two-line labels in the 256px rail.
  // Own keys so a saved 256px width does not keep those panels narrow, and
  // so widening them does not resize the other secondary rails.
  const wideLeftRail =
    panelId === IMPORT_REVIEW_SECONDARY_PANEL_ID || panelId === "library"
  const rail = useShellRailWidth({
    storageKey:
      panelId === IMPORT_REVIEW_SECONDARY_PANEL_ID
        ? "shell:import-review-panel-width"
        : panelId === "library"
          ? "shell:library-panel-width"
          : SECONDARY_PANEL_WIDTH_KEY,
    defaultWidth: wideLeftRail ? 352 : SECONDARY_PANEL_WIDTH_DEFAULT,
    minWidth: wideLeftRail ? 256 : SECONDARY_PANEL_WIDTH_MIN,
    maxWidth: SECONDARY_PANEL_WIDTH_MAX,
    clamp: clampSecondaryPanelWidth,
    version: wideLeftRail ? 3 : 2,
  })
  const PanelContent = panelId ? PANELS[panelId] : null

  if (panelId === "grading-submissions" && !gradingSubmissionsBridge) {
    return null
  }

  if (
    panelId === ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID &&
    !assessmentQuestionBankBridge
  ) {
    return null
  }

  const navFlyout = (isMobile || reflowZoom) && Boolean(panelId)
  const showScopeFlyout =
    navFlyout && secondaryFlyoutVisible && !secondaryFlyoutHidden

  if (navFlyout && !showScopeFlyout) {
    return null
  }

  const shellOpen = Boolean(panelId) && (!navFlyout || secondaryFlyoutVisible)
  const overlay: false | "mobile" | "desktop" = navFlyout
    ? isMobile
      ? "mobile"
      : "desktop"
    : false
  const compact = overlay ? false : secondaryPanelCompact
  const dockedResizable = shellOpen && !compact && overlay === false

  const flyoutChrome = showScopeFlyout ? (
    <>
      <Shortcut keys="Escape" onInvoke={closeSecondaryFlyout} />
      <button
        type="button"
        aria-label="Close secondary navigation"
        className="fixed inset-0 z-[60] cursor-default bg-background/60"
        onClick={closeSecondaryFlyout}
      />
    </>
  ) : null

  const shell = (
    <NestedSecondaryPanelShell
      open={shellOpen}
      compact={compact}
      overlay={overlay}
      ownSize={!dockedResizable && !(shellOpen && compact)}
    >
      {PanelContent ? <PanelContent /> : null}
    </NestedSecondaryPanelShell>
  )

  if (overlay !== false) {
    if (!shellOpen) return null
    return (
      <>
        {flyoutChrome}
        {shell}
      </>
    )
  }

  if (!shellOpen) return null

  if (compact) {
    return (
      <ResizablePanel
        id="shell-secondary-compact"
        defaultSize="3rem"
        minSize="3rem"
        maxSize="3rem"
        groupResizeBehavior="preserve-pixel-size"
        collapsible={false}
        className="min-h-0 min-w-0"
      >
        <NestedSecondaryPanelShell open compact overlay={false} ownSize={false}>
          {PanelContent ? <PanelContent /> : null}
        </NestedSecondaryPanelShell>
      </ResizablePanel>
    )
  }

  return (
    <>
      <ResizablePanel
        id="shell-secondary"
        {...rail.panelProps}
        className="min-h-0 min-w-0"
      >
        {shell}
      </ResizablePanel>
      <ResizableHandle withHandle />
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Auto-open hook — pages call this to show/hide a panel on mount/unmount
// ─────────────────────────────────────────────────────────────────────────────

export function useAutoPanel(panelId: string, opts?: OpenPanelOptions) {
  const { openPanel, closePanel } = useSecondaryPanel()
  const compact = opts?.compact ?? false

  React.useEffect(() => {
    openPanel(panelId, opts)
    return () => {
      closePanel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelId, compact])
}

/** Sync hub folder state into the library secondary nav while the route is mounted. */
export function LibraryFolderBridge({
  folders,
  onFoldersChange,
  onItemsChange,
  folderItemCounts,
  scopeCounts,
}: LibraryFolderBridge) {
  const { setLibraryFolderBridge } = useSecondaryPanel()

  React.useEffect(() => {
    setLibraryFolderBridge({
      folders,
      onFoldersChange,
      onItemsChange,
      folderItemCounts,
      scopeCounts,
    })
    return () => setLibraryFolderBridge(null)
  }, [
    folders,
    onFoldersChange,
    onItemsChange,
    folderItemCounts,
    scopeCounts,
    setLibraryFolderBridge,
  ])

  return null
}

export function LibraryAccessBridge({ openManageAccess }: LibraryAccessBridge) {
  const { setLibraryAccessBridge } = useSecondaryPanel()

  React.useEffect(() => {
    setLibraryAccessBridge({ openManageAccess })
    return () => setLibraryAccessBridge(null)
  }, [openManageAccess, setLibraryAccessBridge])

  return null
}

/** Sync group folders into the learning activities secondary nav while the hub is mounted. */
export function LearningActivitiesFolderBridge({
  folders,
  onFoldersChange,
}: LearningActivitiesFolderBridge) {
  const { setLearningActivitiesFolderBridge } = useSecondaryPanel()

  React.useEffect(() => {
    setLearningActivitiesFolderBridge({ folders, onFoldersChange })
    return () => setLearningActivitiesFolderBridge(null)
  }, [folders, onFoldersChange, setLearningActivitiesFolderBridge])

  return null
}

/** Sync submission roster into the grading submissions secondary nav on detail routes. */
export function GradingSubmissionsBridge({
  state,
  submissions,
  currentSubmissionId,
  onSelect,
}: GradingSubmissionsBridge) {
  const { setGradingSubmissionsBridge } = useSecondaryPanel()

  React.useEffect(() => {
    setGradingSubmissionsBridge({
      state,
      submissions,
      currentSubmissionId,
      onSelect,
    })
    return () => setGradingSubmissionsBridge(null)
  }, [
    state,
    submissions,
    currentSubmissionId,
    onSelect,
    setGradingSubmissionsBridge,
  ])

  return null
}

/** Sync add-questions navigator into the shell secondary rail while the pool is mounted. */
export function AssessmentQuestionBankBridge({
  navGroups,
  scope,
  onScopeChange,
  leftTab,
  onLeftTabChange,
  blueprintGapsLength,
  assessmentGapProgress,
  assessmentRows,
  discriminationMethodId,
  onDiscriminationMethodChange,
  onFocusBlueprintTarget,
  onSelectMixBand,
  activeMixFacetScope,
  sectionId,
  addedRows,
  sectionPaperRows,
  onSectionDragEnd,
  onPreviewPaperRow,
  onMoveSectionQuestion,
  onRemovePaperRows,
  onEditPaperRow,
  totalPoints,
  estimatedMinutes,
  needsReviewCount,
}: AssessmentQuestionBankBridge) {
  const { setAssessmentQuestionBankBridge } = useSecondaryPanel()

  React.useEffect(() => {
    setAssessmentQuestionBankBridge({
      navGroups,
      scope,
      onScopeChange,
      leftTab,
      onLeftTabChange,
      blueprintGapsLength,
      assessmentGapProgress,
      assessmentRows,
      discriminationMethodId,
      onDiscriminationMethodChange,
      onFocusBlueprintTarget,
      onSelectMixBand,
      activeMixFacetScope,
      sectionId,
      addedRows,
      sectionPaperRows,
      onSectionDragEnd,
      onPreviewPaperRow,
      onMoveSectionQuestion,
      onRemovePaperRows,
      onEditPaperRow,
      totalPoints,
      estimatedMinutes,
      needsReviewCount,
    })
    return () => setAssessmentQuestionBankBridge(null)
  }, [
    navGroups,
    scope,
    onScopeChange,
    leftTab,
    onLeftTabChange,
    blueprintGapsLength,
    assessmentGapProgress,
    assessmentRows,
    discriminationMethodId,
    onDiscriminationMethodChange,
    onFocusBlueprintTarget,
    onSelectMixBand,
    activeMixFacetScope,
    sectionId,
    addedRows,
    sectionPaperRows,
    onSectionDragEnd,
    onPreviewPaperRow,
    onMoveSectionQuestion,
    onRemovePaperRows,
    onEditPaperRow,
    totalPoints,
    estimatedMinutes,
    needsReviewCount,
    setAssessmentQuestionBankBridge,
  ])

  return null
}
